import {ReactNode, useEffect, useState} from 'react';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Loading} from '../../../../../../design-system/atoms/Loading/Loading';
import {ImageViewerData} from '../../../../../../graphql/generated/types';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {
  setCurrentPage,
  setDownloadData,
  setIsViewerEnd,
  setTotalPage,
} from '../state/imageViewerSlice';
import {setIsViewerEndExposed} from '../state/viewerEndSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {restoreFromLastViewRate} from '../util/restoreFromLastViewRate';

/**
 * 이미지 뷰어는 다음과 같은 기준으로 변수를 사용합니다.
 * page -> 1부터 N까지의 실제 페이지를 나타냅니다.
 * index -> 0부터 N-1까지의 배열이나 swiper, data-index의 인덱스를 나타냅니다.
 * progress -> 0부터 1까지의 비율을 나타냅니다. 일반적으로 (currentPage-1)/(totalPage-1) 입니다.
 * rate -> currentPage/totalPage로 나타내지는 비율입니다. 여기에 10000을 곱해 만분율로 마지막 페이지를 저장합니다.(안드로이드, iOS 동일)
 **/

export function ImageViewerFetcherHeadless({
  seriesId,
  productId,
  children,
}: {
  seriesId: number;
  productId: number;
  children: ReactNode;
}) {
  const [isLoaded, setIsLoaded] = useState<boolean>();

  const {isFromPrevProduct, currentPage} = useTypedSelector(state => {
    const isFromPrevProduct = state.viewerInfo.isFromPrevProduct;
    return {
      isFromPrevProduct,
      currentPage: state.imageViewer.viewData[productId]?.currentPage,
    };
  });
  const viewerInfoData = useViewerInfoQuery({seriesId, productId}).data;
  const downloadDataInViewerInfo = (
    viewerInfoData?.viewerData as ImageViewerData
  )?.imageDownloadData;

  const dispatch = useTypedDispatch();

  useEffect(() => {
    return () => {
      dispatch(setCurrentPage({productId, page: 1}));
    };
  }, [dispatch, productId]);

  useEffect(() => {
    dispatch(setIsViewerEndExposed(true));
    dispatch(setIsViewerMenuExposed({isExposed: true}));
  }, [dispatch]);

  useEffect(() => {
    setIsLoaded(false);
    dispatch(
      setDownloadData({productId, downloadData: downloadDataInViewerInfo})
    );
  }, [downloadDataInViewerInfo, dispatch, productId]);

  const downloadData = useTypedSelector(
    state => state.imageViewer.viewData[productId]?.downloadData
  );

  const lastViewInfoInViewerInfo =
    viewerInfoData?.item?.serviceProperty?.lastViewInfo;

  useEffect(() => {
    if (!downloadData || isLoaded) {
      return;
    }
    const rate = isFromPrevProduct ? 0 : lastViewInfoInViewerInfo?.rate || 0;
    const isViewerEnd = isFromPrevProduct
      ? false
      : lastViewInfoInViewerInfo?.spineIndex === 1;

    const page =
      restoreFromLastViewRate({
        rate,
        total: downloadData.files.length,
      }) ||
      currentPage ||
      0;

    dispatch(
      setCurrentPage({
        productId,
        page: isViewerEnd ? downloadData.files.length + 1 : page,
      })
    );
    dispatch(setIsViewerEnd({productId, isViewerEnd}));
    dispatch(
      setTotalPage({
        productId,
        totalPage: downloadData.files.length || 1,
      })
    );

    // XXX: 혹시 프리로드가 필요하면 여기서 로드 완료 상태로 바꾸지 말고,
    // 각 이미지 뷰어 컨테이너 내에서 완료 상태로 바꾸는게 좋겠습니다.
    setIsLoaded(true);
  }, [
    currentPage,
    dispatch,
    downloadData,
    isFromPrevProduct,
    isLoaded,
    lastViewInfoInViewerInfo?.rate,
    lastViewInfoInViewerInfo?.spineIndex,
    productId,
  ]);

  return (
    <>
      {!isLoaded && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
      {isLoaded && children}
    </>
  );
}
